import { memo, useState, useCallback, useEffect, useRef } from 'react'

import { CS, CS_m } from 'back-end-api'
import useChat from 'hooks/twilio/useChat'
import useVideo, { ROOM_STATUSES } from 'hooks/twilio/useVideo'
import { useMiscDispatch } from 'services/Misc'
import { getClaims } from 'services/Authentication'
import {
  setHangUpAppointment,
  useAudioVisualsState,
  setAppointmentPickedUp,
  setAppointmentSnoozed,
  setInProgressAppointment,
  clearRequestOpenAppointmentCall,
} from 'services/Misc/audioVisuals'

import ResumeAVCall from './ResumeAVCall'
import VideoComp from './VideoComp'
import InfoBox from './InfoBox'
import IncomingCallPopup from './IncomingCallPopup'
import { Container, VideoGridContainer } from './Styles'
import { containerVariants, infoColumnVariants } from './Animations'

const AVConsult = () => {
  const miscDispatch = useMiscDispatch()
  const audioVisualState = useAudioVisualsState()
  
  // Ref to track appointments that have been processed (picked up or snoozed)
  // This prevents the modal from appearing again due to SWR cache updates.
  // Cleared for a given appointment once its status moves to a final state,
  // so that a NEW rescheduled appointment (different ID) is never blocked.
  const processedAppointmentIdsRef = useRef<Set<number>>(new Set())
  
  // Track if we've already checked for in-progress appointments on mount
  const hasCheckedInProgressRef = useRef(false)

  const { data, isValidating } = CS.useRegistrationsList({
    without_old_closed_summaries: 'true',
  })

  const [newAppointmentReady, setNewAppointmentReady] = useState<
    CS_m.ModelsAppointment | undefined
  >(undefined)
  
  // Track in-progress appointment that needs to be resumed
  const [inProgressAppointment, setInProgressAppointmentState] = useState<
    CS_m.ModelsAppointment | undefined
  >(undefined)

  const [videoToken, setVideoToken] = useState('')
  const [videoRoomName, setVideoRoomName] = useState('')

  const { messageList, typing, onTyping, close, sendMessage } = useChat(videoToken, videoRoomName)
  const { tracks, status, setMic, setCam, hangUp, inError } = useVideo(videoToken, videoRoomName)
  const [microphoneActive, setMicrophoneActive] = useState(true)
  const [videoActive, setVideoActive] = useState(true)
  const [generalLoading, setGeneralLoading] = useState(false)
  const [isProcessingAction, setIsProcessingAction] = useState(false)
  const [size, setSize] = useState({
    small: false,
    medium: true,
    large: false,
  })
  const [showInfo, setShowInfo] = useState(false)
  const [sidebarContent, setSidebarContent] = useState('')

  const onPickup = useCallback(() => {
    if (!newAppointmentReady || isProcessingAction) {
      return
    }
    setIsProcessingAction(true)
    // Mark this appointment as processed to prevent it from showing again
    processedAppointmentIdsRef.current.add(newAppointmentReady.id)
    setAppointmentPickedUp(miscDispatch, audioVisualState, newAppointmentReady)
    setNewAppointmentReady(undefined)
    // Reset processing state after a short delay
    setTimeout(() => setIsProcessingAction(false), 1000)
  }, [miscDispatch, audioVisualState, newAppointmentReady, isProcessingAction])

  const onSnooze = useCallback(() => {
    if (!newAppointmentReady || isProcessingAction) {
      return
    }
    setIsProcessingAction(true)
    // Mark this appointment as processed to prevent it from showing again
    processedAppointmentIdsRef.current.add(newAppointmentReady.id)
    setAppointmentSnoozed(miscDispatch, audioVisualState, newAppointmentReady)
    setNewAppointmentReady(undefined)
    // Reset processing state after a short delay
    setTimeout(() => setIsProcessingAction(false), 1000)
  }, [miscDispatch, audioVisualState, newAppointmentReady, isProcessingAction])

  // Handler to resume an in-progress call
  const onResume = useCallback(() => {
    if (!inProgressAppointment || isProcessingAction) {
      return
    }
    setIsProcessingAction(true)
    // Clear the in-progress state first
    setInProgressAppointmentState(undefined)
    // Set the appointment as picked up to rejoin the call
    setAppointmentPickedUp(miscDispatch, audioVisualState, inProgressAppointment)
    // Reset processing state after a short delay
    setTimeout(() => setIsProcessingAction(false), 1000)
  }, [miscDispatch, audioVisualState, inProgressAppointment, isProcessingAction])

  // Handler to dismiss the resume prompt (end the call without rejoining)
  const onDismissResume = useCallback(async () => {
    if (!inProgressAppointment || isProcessingAction) {
      return
    }
    setIsProcessingAction(true)
    
    // Update the appointment status to done
    await CS.updateAppointment({
      id: inProgressAppointment.id,
      status: CS_m.EndpointsUpdateAppointmentRequestStatus.done,
    })
    
    setInProgressAppointmentState(undefined)
    setInProgressAppointment(miscDispatch, audioVisualState, null)
    setTimeout(() => setIsProcessingAction(false), 1000)
  }, [miscDispatch, audioVisualState, inProgressAppointment, isProcessingAction])

  const handleResize = useCallback((newValue: string) => {
    switch (newValue) {
      case 'small':
        return setSize({ large: false, medium: false, small: true })
      case 'medium':
        return setSize({ large: false, medium: true, small: false })
      case 'large':
        return setSize({ large: true, medium: false, small: false })
    }
    return true
  }, [])

  const handleShowInfo = useCallback(() => setShowInfo(v => !v), [])

  // Track the appointment ID we're currently joining/in-call-for to prevent
  // joining a stale or already-hung-up appointment.
  const joiningAppointmentIdRef = useRef<number | null>(null)

  const handleHangUp = useCallback(async () => {
    // Store the appointment ID before clearing state
    const appointmentId = audioVisualState.pickedUpAppointmentId
    
    setMicrophoneActive(true)
    setVideoActive(true)
    setVideoToken('')
    setVideoRoomName('')
    setNewAppointmentReady(undefined)
    setHangUpAppointment(miscDispatch, audioVisualState)

    // Remove from processed set so it doesn't block future detection.
    // Once an appointment is "done" the status filter excludes it anyway,
    // and keeping stale IDs in the set could mask bugs in edge cases.
    if (appointmentId) {
      processedAppointmentIdsRef.current.delete(appointmentId)
    }

    // Reset the joining tracker so the same appointment can be re-joined
    // if the doctor picks it up again (e.g. after a disconnect/reconnect).
    joiningAppointmentIdRef.current = null

    // Only update appointment if we have a valid ID
    // When patient closes first, the backend may have already updated the status
    if (appointmentId && appointmentId > 0) {
      const result = await CS.updateAppointment({
        id: appointmentId,
        status: CS_m.EndpointsUpdateAppointmentRequestStatus.done,
      })
      
      if (result.error) {
        // Log but don't fail - the appointment may already be updated by the backend
        console.warn('Failed to update appointment status during hangup:', result.error)
      }
    }

    await hangUp()
    await close()
  }, [audioVisualState, miscDispatch, close, hangUp])

  useEffect(() => {
    // Only act on disconnection if we actually have an active picked-up call.
    // Without this guard, a stale DISCONNECTED status from a previous Twilio room
    // could trigger hangup and mark a rescheduled appointment as done prematurely.
    if (status === ROOM_STATUSES.DISCONNECTED && audioVisualState.pickedUpAppointmentId) {
      handleHangUp()
    }
  }, [status, handleHangUp, audioVisualState.pickedUpAppointmentId])

  const animate = useCallback(() => {
    if (size.medium) {
      return 'visible'
    }

    if (size.small) {
      return 'small'
    }

    return 'large'
  }, [size])

  // Separate effect to clear ready appointment when one is picked up
  useEffect(() => {
    if (audioVisualState.pickedUpAppointmentId) {
      setNewAppointmentReady(undefined)
      setInProgressAppointmentState(undefined)
    }
  }, [audioVisualState.pickedUpAppointmentId])

  // Effect to detect in-progress appointments on page load (for reconnection)
  useEffect(() => {
    if (hasCheckedInProgressRef.current || isValidating || audioVisualState.pickedUpAppointmentId) {
      return
    }
    
    if (data && data.length > 0) {
      hasCheckedInProgressRef.current = true
      
      // Find any appointment that is in_progress (doctor was in a call but disconnected)
      const summaries = CS.getSummaries(data)
      
      // Find the first in-progress appointment with its summary attached
      const inProgressAppt = summaries.reduce<CS_m.ModelsAppointment | undefined>((found, summary) => {
        if (found) return found
        const appt = summary.appointments?.find(
          a => a.status === CS_m.ModelsAppointmentStatus.inProgress
        )
        if (appt) {
          // Attach the summary to the appointment for patient info display
          return { ...appt, summary }
        }
        return undefined
      }, undefined)
      
      if (inProgressAppt) {
        console.log('[AVConsult] Found in-progress appointment to resume:', inProgressAppt.id)
        setInProgressAppointmentState(inProgressAppt)
        setInProgressAppointment(miscDispatch, audioVisualState, inProgressAppt.id)
      }
    }
  }, [data, isValidating, audioVisualState, miscDispatch])

  useEffect(() => {
    // Don't process if we're currently handling an action or already have a picked up appointment
    if (isProcessingAction || audioVisualState.pickedUpAppointmentId) {
      return
    }
    
    if (!isValidating) {
      const claims = getClaims()
      const currentDoctorId = claims?.sub ? parseInt(claims.sub, 10) : 0

      // Fix: Get ALL appointments from ALL summaries, not just the first one per summary
      // Also carry the registration-level doctor_account_id for ownership checks
      const allAppointments = (data ?? [])
        .flatMap(registration => {
          const summary = CS.getSummary(registration)
          if (!summary?.appointments) return []
          return summary.appointments.map(appointment => ({
            ...appointment,
            summary: { ...summary, registration },
            registrationDoctorId: registration.doctor_account_id,
          }))
        })

      // If the user has explicitly requested to re-open a specific call
      // (from the appointments list phone icon), honour that request first
      // and bypass the snooze/processed filters.
      const requestedId = audioVisualState.requestedOpenAppointmentId
      const requestedAppointment = requestedId
        ? allAppointments.find(
            a =>
              a.id === requestedId &&
              (a.status === CS_m.ModelsAppointmentStatus.ready ||
                a.status === CS_m.ModelsAppointmentStatus.inProgress) &&
              (a.doctor_account_id === currentDoctorId ||
                a.registrationDoctorId === currentDoctorId)
          )
        : undefined

      if (requestedAppointment) {
        processedAppointmentIdsRef.current.delete(requestedAppointment.id)
        setNewAppointmentReady(prev =>
          prev?.id === requestedAppointment.id ? prev : requestedAppointment
        )
        // Clear the request flag so we don't keep re-opening on every poll
        clearRequestOpenAppointmentCall(miscDispatch, audioVisualState)
        return
      }

      const readyAppointments = allAppointments.filter(a => a.status === CS_m.ModelsAppointmentStatus.ready)

      // Show popup if this doctor initiated the call OR if the registration is owned by this doctor
      const afterDoctorFilter = readyAppointments.filter(a =>
        a.doctor_account_id === currentDoctorId ||
        a.registrationDoctorId === currentDoctorId
      )
      const afterSnoozeFilter = afterDoctorFilter.filter(a => !audioVisualState.appointmentSnoozedIds.some(snoozedId => a.id === snoozedId))

      const afterProcessedFilter = afterSnoozeFilter.filter(a => !processedAppointmentIdsRef.current.has(a.id))

      const appointmentReady = afterProcessedFilter.find(() => true) // Get the first one

      // Only update if the appointment is different to prevent duplicate renders
      setNewAppointmentReady(prev => {
        // If we already have this appointment showing, don't update
        if (prev?.id === appointmentReady?.id) {
          return prev
        }
        // If the new appointment was already processed, don't show it
        if (appointmentReady && processedAppointmentIdsRef.current.has(appointmentReady.id)) {
          return prev
        }
        return appointmentReady
      })
    }
  }, [data, isValidating, audioVisualState, isProcessingAction, miscDispatch])

  useEffect(() => {
    if (audioVisualState.pickedUpAppointmentId && !generalLoading && videoToken === '') {
      // Guard: don't re-join if we already joined this appointment
      if (joiningAppointmentIdRef.current === audioVisualState.pickedUpAppointmentId) return
      joiningAppointmentIdRef.current = audioVisualState.pickedUpAppointmentId
      setGeneralLoading(true)
      CS.joinAppointmentVideo({ id: audioVisualState.pickedUpAppointmentId }).then(res => {
        if (res.data) {
          setVideoToken(res.data.token)
          setVideoRoomName(res.data.video_room_id)
        }
        setGeneralLoading(false)
      })
    } else if (!audioVisualState.pickedUpAppointmentId) {
      // Reset joining tracker when no appointment is active
      joiningAppointmentIdRef.current = null
    }
  }, [audioVisualState, videoToken, generalLoading])

  useEffect(() => {
    if (inError) {
      console.error('Error in video call')
      handleHangUp()
    }
  }, [inError, handleHangUp])

  if (audioVisualState.pickedUpAppointmentId) {
    return (
      <>
        <Container initial="hidden" animate={animate()} variants={containerVariants}>
          <VideoGridContainer showInfo={showInfo} large={size.large} small={size.small}>
            <VideoComp
              status={status}
              tracks={tracks}
              setMic={setMic}
              setCam={setCam}
              hangUp={handleHangUp}
              microphoneActive={microphoneActive}
              setMicrophoneActive={setMicrophoneActive}
              videoActive={videoActive}
              setVideoActive={setVideoActive}
              openMessages={handleShowInfo}
              setSidebarContent={setSidebarContent}
              handleResize={handleResize}
              small={size.small}
              medium={size.medium}
              large={size.large}
              showInfo={showInfo}
              sidebarContent={sidebarContent}
            />
            <InfoBox
              small={size.small}
              large={size.large}
              onCloseInfo={handleShowInfo}
              initial="hidden"
              animate={showInfo && (size.large || size.medium) ? 'visible' : 'hidden'}
              variants={infoColumnVariants}
              sidebarContent={sidebarContent}
              setSidebarContent={setSidebarContent}
              showInfo={showInfo}
              list={messageList.items}
              typing={typing}
              onSubmit={sendMessage}
              onKeyPress={onTyping}
            />
          </VideoGridContainer>
        </Container>
      </>
    )
  }

  if (newAppointmentReady) {
    return (
      <IncomingCallPopup
        appointment={newAppointmentReady}
        onPickup={onPickup}
        onPickupLater={onSnooze}
      />
    )
  }

  // Show resume prompt if there's an in-progress appointment
  if (inProgressAppointment) {
    return (
      <Container initial="hidden" animate={animate()} variants={containerVariants}>
        <VideoGridContainer showInfo={showInfo} large={size.large} small={size.small}>
          <ResumeAVCall 
            appointment={inProgressAppointment} 
            onResume={onResume} 
            onDismiss={onDismissResume} 
          />
        </VideoGridContainer>
      </Container>
    )
  }

  return <></>
}

AVConsult.whyDidYouRender = true

export default memo(AVConsult)
